import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { isEqual } from 'lodash';
import React, {
  FC,
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PopoverBody,
  Row,
  UncontrolledPopover,
} from 'reactstrap';
import {
  type Person,
  type ReduxState,
  type UserGeneratedInputType,
} from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useMe, useUnsavedChangesWarning } from 'utils/util/hooks';
import {
  formatWallClockAsNaiveIsoString,
  getBrowserTimezone,
  getTimezoneOption,
  getWallClockInTimeZone,
  type TimezoneOption,
} from 'utils/util/timezone';
import {
  apiFilterHasValues,
  canonicalizeApiFilter,
  DRAG_AND_DROP_ICON,
  DraggableItemTypes,
  getFriendlyErrorMessageIfErrorOccurred,
  useDragAndDrop,
} from 'utils/util/util';
import EmptyState from 'views/Widgets/EmptyState';
import {
  AdvancedCurrentPeopleApiFilter,
  AdvancedCurrentPeopleFilterContextProvider,
  useAdvancedCurrentPeopleFilter,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import { type ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import QuestionEditor from 'views/Widgets/Inputs/QuestionEditor';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import AdminScopeDisclosure from './AdminScopeDisclosure';
import {
  ALLOWED_QUESTION_TYPES,
  CADENCE_OPTIONS,
  type CustomSurveyData,
  type CustomSurveyQuestionData,
  EXCLUDE_SPECIAL_QUESTIONS,
  MILESTONE_FIELD_OPTIONS,
  NAME_DESCRIPTION_INPUTS,
  QUESTION_TYPES,
  SCHEDULE_INPUTS,
  SURVEY_STATUSES,
  SURVEY_TYPES,
  type SurveyQuestionAssociation,
  validateCustomSurveySettings,
} from './consts';
import ScheduledParticipantsField from './ScheduledParticipantsField';
import TargetFilterLabel from './TargetFilterLabel';

const STATUS_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['LIVE', 'ARCHIVED'],
  LIVE: ['PAUSED', 'ARCHIVED'],
  PAUSED: ['LIVE', 'ARCHIVED'],
  ARCHIVED: ['LIVE'],
};

const STATUS_COLOR: Record<string, string> = {
  LIVE: 'success',
  PAUSED: 'warning',
  DRAFT: 'secondary',
  ARCHIVED: 'secondary',
};

const QuestionFilterSummary: FC<{ filter: ApiGroup }> = ({ filter }) => {
  const { formatMessage } = useIntl();
  const { filterToText } = useAdvancedCurrentPeopleFilter();
  const text = filterToText(filter);
  if (!text) return null;
  return (
    <small className="text-primary fst-italic d-block mt-1">
      <Icon className="me-1" icon={consts.ICONS.EYE} />
      {formatMessage(
        {
          id: 'app.views.administration.custom_surveys.surveys.editor.question_filter_summary',
          defaultMessage: 'Only visible to: {filter}',
        },
        { filter: text }
      )}
    </small>
  );
};

const FilterSummaryInput: FC<{ value?: ApiGroup; filterError?: string }> = ({
  value,
  filterError,
}) => {
  if (filterError) {
    return (
      <small className="text-danger d-block mt-1">
        <Icon className="me-1" icon={consts.ICONS.ALERT} />
        {filterError}
      </small>
    );
  }
  if (!value || Object.keys(value).length === 0) return null;
  return <QuestionFilterSummary filter={value} />;
};

interface SelectedQuestionRowProps {
  association: SurveyQuestionAssociation;
  index: number;
  questionTypes: ReturnType<typeof QUESTION_TYPES>;
  moveQuestion: (dragIndex: number, hoverIndex: number) => void;
  onToggleRequired: (index: number) => void;
  onOpenFilter: (index: number) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
  // Set from the last save attempt's {"questions": {uuid: message}} error
  // response (see handleSave) - a stale filter that no longer matches
  // anyone in the survey's audience.
  filterError?: string;
}

const SelectedQuestionRow: FC<SelectedQuestionRowProps> = ({
  association: q,
  index,
  questionTypes,
  moveQuestion,
  onToggleRequired,
  onOpenFilter,
  onRemove,
  disabled,
  filterError,
}) => {
  const { formatMessage } = useIntl();

  const [dragAndDropRef, handlerId, drop, isDragging, drag] = useDragAndDrop(
    DraggableItemTypes.QUESTION,
    `${q.question_uuid}-${index}`,
    index,
    moveQuestion,
    !disabled
  );
  // @ts-expect-error - useDragAndDrop is untyped JS
  drag(drop(dragAndDropRef));

  const hasFilter = q.target_filter && Object.keys(q.target_filter).length > 0;

  return (
    <div
      // @ts-expect-error - untyped ref from useDragAndDrop
      ref={dragAndDropRef}
      data-handler-id={handlerId}
      className="list-group-item d-flex align-items-center justify-content-between gap-3"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <div className="d-flex align-items-center flex-grow-1">
        <div
          role="button"
          className="me-2 text-muted"
          style={{
            cursor: disabled ? 'default' : 'grab',
            opacity: disabled ? 0.4 : 1,
          }}
          aria-label={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.drag_handle_aria',
            defaultMessage: 'Drag to reorder',
          })}
        >
          {DRAG_AND_DROP_ICON}
        </div>
        <div className="flex-1">
          <div className="fw-bold">
            <RichTextViewer
              model={
                (q.question_data?.label as string) ||
                (q.question?.label as string) ||
                q.question_label ||
                formatMessage({
                  id: 'app.views.administration.custom_surveys.surveys.editor.untitled_question',
                  defaultMessage: 'Untitled question',
                })
              }
            />
          </div>
          <small className="text-muted">
            {questionTypes.find(
              (t) =>
                t.id ===
                (q.question_data?.type || q.question?.type || q.question_type)
            )?.name ||
              q.question_type ||
              ''}
          </small>
          {filterError ? (
            <small className="text-danger d-block mt-1">
              <Icon className="me-1" icon={consts.ICONS.ALERT} />
              {filterError}
            </small>
          ) : (
            hasFilter && (
              <QuestionFilterSummary
                filter={q.target_filter as unknown as ApiGroup}
              />
            )
          )}
        </div>
      </div>
      <div className="d-flex align-items-center flex-shrink-0">
        <div className="form-check me-3">
          <input
            type="checkbox"
            className="form-check-input"
            checked={q.required}
            onChange={() => onToggleRequired(index)}
            disabled={disabled}
            id={`required-${index}`}
          />
          <label className="form-check-label" htmlFor={`required-${index}`}>
            <FormattedMessage
              id="app.views.administration.custom_surveys.surveys.editor.required"
              defaultMessage="Required"
            />
          </label>
        </div>
        <button
          className="btn btn-sm btn-light p-1 px-2 me-2"
          onClick={() => onOpenFilter(index)}
          disabled={disabled}
          aria-label={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.filter_aria',
            defaultMessage: 'Edit question audience filter',
          })}
        >
          <i
            className={`fe fe-filter pe-1${hasFilter ? ' text-primary' : ''}`}
          />
          <FormattedMessage
            id="app.views.administration.custom_surveys.surveys.editor.filter"
            defaultMessage="Filter"
          />
        </button>
        <button
          className="btn btn-sm btn-light p-1 px-2"
          onClick={() => onRemove(index)}
          disabled={disabled}
          aria-label={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.remove_aria',
            defaultMessage: 'Remove question',
          })}
        >
          <FormattedMessage
            id="app.views.administration.custom_surveys.surveys.editor.remove"
            defaultMessage="Remove"
          />
        </button>
      </div>
    </div>
  );
};

interface Props {
  // Owned by the page so the header can title itself without fetching the
  // survey a second time (useConfirmApi neither caches nor dedupes).
  survey: CustomSurveyData | null;
  isLoading: boolean;
  // Status changes and saves return a fresh survey; handing it back keeps the
  // page's copy (and so the header) in step.
  onSurveyChange: (survey: CustomSurveyData) => void;
  // Set to this tab's unsaved-changes guard so the page around it can ask
  // before navigating away or switching tabs. useUnsavedChangesWarning only
  // covers a browser unload, so anything in-app has to call the guard itself.
  leaveGuardRef?: MutableRefObject<(() => boolean) | null>;
}

const CustomSurveyBasicsTab: FC<Props> = ({
  survey,
  isLoading,
  onSurveyChange,
  leaveGuardRef,
}) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const proxyParameters = useProxyParameters();
  const me = useMe();
  const currentProxyPerson = useSelector<ReduxState, Person | null>(
    (state) => state?.currentProxyPerson
  );

  const [selectedQuestions, setSelectedQuestions] = useState<
    SurveyQuestionAssociation[]
  >([]);
  const [availableQuestions, setAvailableQuestions] = useState<
    CustomSurveyQuestionData[]
  >([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isQuestionEditorOpen, setIsQuestionEditorOpen] = useState(false);
  const [filterEditingIndex, setFilterEditingIndex] = useState<number | null>(
    null
  );
  const [pendingFilter, setPendingFilter] = useState<ApiGroup | undefined>(
    undefined
  );
  const [settingsObj, setSettingsObj] = useState<CustomSurveyData | undefined>(
    undefined
  );
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const settingsFormRef = useRef<HTMLFormElement | null>();

  // Seed the form from the survey the page loaded, once. Later replacements
  // (a status change, a save) deliberately don't re-seed settingsObj - that
  // would throw away whatever is currently in the form.
  const hasSeededForm = useRef(false);
  useEffect(() => {
    if (!survey || hasSeededForm.current) return;
    hasSeededForm.current = true;
    {
      const data = survey;
      setSelectedQuestions(data.questions_at_latest_version || []);
      setSettingsObj({
        status: data.status,
        name: data.name || '',
        description: data.description || '',
        survey_type: data.survey_type || '',
        cadence: data.cadence ?? null,
        milestone_days_offset: data.milestone_days_offset ?? null,
        milestone_field: data.milestone_field ?? null,
        scheduled_time: data.scheduled_time
          ? (getWallClockInTimeZone(
              new Date(data.scheduled_time),
              data.scheduled_timezone || getBrowserTimezone()
            ) as unknown as string)
          : null,
        scheduled_timezone: data.scheduled_timezone
          ? (getTimezoneOption(data.scheduled_timezone) as unknown as string)
          : null,
        days_to_respond: data.days_to_respond ?? null,
        scheduled_participants: (data.scheduled_participants ?? []).map(
          (participant) => ({
            ...participant,
            scheduled_time: getWallClockInTimeZone(
              new Date(participant.scheduled_time),
              participant.scheduled_timezone
            ) as unknown as string,
          })
        ),
        target_filter:
          data.target_filter && Object.keys(data.target_filter).length > 0
            ? (data.target_filter as unknown as ApiGroup)
            : undefined,
      });
    }
  }, [survey]);

  // Fetch available questions
  const questionsCallback = useCallback(
    (
      data: CustomSurveyQuestionData[] | null,
      error: unknown,
      hardError: unknown
    ) => {
      if (!data || error || hardError) return;
      setAvailableQuestions(data);
    },
    []
  );

  useConfirmApi<CustomSurveyQuestionData[]>({
    method: 'GET',
    url: 'custom-survey/questions/admin',
    callback: questionsCallback,
  });

  const confirmLeaveWithUnsavedChanges = useCallback(
    () =>
      window.confirm(
        formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.unsaved_warning',
          defaultMessage:
            'You have unsaved changes. Are you sure you want to leave?',
        })
      ),
    [formatMessage]
  );
  const canLeavePage = useUnsavedChangesWarning(
    hasUnsavedChanges,
    confirmLeaveWithUnsavedChanges
  );

  useEffect(() => {
    if (!leaveGuardRef) return;
    leaveGuardRef.current = canLeavePage;
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef, canLeavePage]);

  const questionTypes = useMemo(
    () => QUESTION_TYPES(formatMessage),
    [formatMessage]
  );
  const surveyTypes = useMemo(
    () => SURVEY_TYPES(formatMessage),
    [formatMessage]
  );
  const cadenceOptions = useMemo(
    () => CADENCE_OPTIONS(formatMessage),
    [formatMessage]
  );
  const milestoneFieldOptions = useMemo(
    () => MILESTONE_FIELD_OPTIONS(formatMessage),
    [formatMessage]
  );
  const surveyStatuses = useMemo(
    () => SURVEY_STATUSES(formatMessage),
    [formatMessage]
  );

  const previewInputs = useMemo(
    () =>
      selectedQuestions.flatMap((q, index) => {
        const qData = (q.question_data || q.question || {}) as Record<
          string,
          unknown
        >;
        const hasFilter =
          q.target_filter && Object.keys(q.target_filter).length > 0;
        const questionInput = {
          name: `preview_${index}`,
          label: (
            <RichTextViewer
              model={(qData.label as string) || q.question_label || ''}
            />
          ),
          helperText: (
            <RichTextViewer model={qData.helperText as string | undefined} />
          ),
          type: qData.type,
          required: q.required,
          options: qData.options as
            | Array<{ id: string; name: string }>
            | undefined,
          objects: qData.objects as
            | Array<{ name: string; value: number }>
            | undefined,
        };
        const filterError = q.filterError;
        if (!hasFilter && !filterError) return [questionInput];
        return [
          {
            name: `preview_filter_${index}`,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            component: FilterSummaryInput,
            value: q.target_filter,
            filterError,
          },
          questionInput,
        ];
      }),
    [selectedQuestions]
  );

  const previewObject = useMemo(
    () =>
      Object.fromEntries(
        selectedQuestions.flatMap((q, index) => {
          const hasFilter =
            q.target_filter && Object.keys(q.target_filter).length > 0;
          const entries: [string, unknown][] = [
            [`preview_${index}`, undefined],
          ];
          if (hasFilter) {
            entries.unshift([`preview_filter_${index}`, q.target_filter]);
          }
          return entries;
        })
      ),
    [selectedQuestions]
  );

  const toggleStatusDropdown = useCallback(() => {
    setIsStatusDropdownOpen((prev) => !prev);
  }, []);

  const isSurveyInDraft = useMemo(
    () => survey?.status === 'DRAFT',
    [survey?.status]
  );

  // Mirrors Calibration's isViewOwner: the effectively-current person
  // (accounting for proxy/impersonation) compared against the survey's
  // actual owner - not hardcoded, since whoever's viewing this page could be
  // either the owner or an administrator.
  const viewerIsOwner = useMemo(() => {
    const viewer = currentProxyPerson || me;
    return !!viewer && viewer.id === survey?.owner?.id;
  }, [currentProxyPerson, me, survey?.owner?.id]);

  const isArchived = survey?.status === 'ARCHIVED';

  // A one-time survey that has already been sent can never be reactivated -
  // the scheduler will never resend it once an instance exists - so
  // reactivation is blocked entirely for that case.
  const isReactivationBlocked =
    isArchived &&
    survey?.survey_type === 'ONE_TIME' &&
    (survey?.instance_count ?? 0) > 0;

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (!survey?.id) return;
      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `custom-survey/surveys/${survey.id}`,
        { ...proxyParameters, status: newStatus },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            const backendMsg = getFriendlyErrorMessageIfErrorOccurred(
              error,
              hardErrorMessage
            );
            toast.error(
              backendMsg ??
                formatMessage({
                  id: 'app.views.administration.custom_surveys.surveys.editor.status_error',
                  defaultMessage: 'Failed to update survey status.',
                })
            );
            return;
          }
          onSurveyChange(data);
          toast.success(
            formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.status_success',
              defaultMessage: 'Survey status updated.',
            })
          );
        }
      );
    },
    [survey, proxyParameters, formatMessage, onSurveyChange]
  );

  const settingsInputs = useMemo(
    () => [
      ...NAME_DESCRIPTION_INPUTS(formatMessage, isSaving),
      {
        name: 'survey_type',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.type_label',
          defaultMessage: 'When do you want to send this survey?',
        }),
        objects: surveyTypes,
        required: true,
        clearable: false,
        disabled: !isSurveyInDraft || isSaving,
        // Switching to ONE_TIME or PERIODIC reveals the timezone picker, which
        // for a survey stored as MILESTONE has nothing seeded (the column is
        // NULL for that type). Pre-populate it with this browser's zone so
        // it's never revealed empty. Only when it's actually unset, so
        // toggling types doesn't discard a zone the user already picked.
        onChangeSideEffects: (
          currentObject: Record<string, unknown>,
          newValue: string
        ) =>
          (newValue === 'ONE_TIME' || newValue === 'PERIODIC') &&
          !currentObject.scheduled_timezone
            ? { scheduled_timezone: getTimezoneOption(getBrowserTimezone()) }
            : {},
      },
      ...(settingsObj?.survey_type === 'PER_PERSON'
        ? [
            {
              name: 'scheduled_participants',
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              component: ScheduledParticipantsField,
              targetFilter: settingsObj?.target_filter ?? null,
              disabled: isSaving,
              showSentFilter: true,
            },
          ]
        : []),
      ...(settingsObj?.survey_type === 'PERIODIC'
        ? [
            {
              name: 'cadence',
              type: INPUT_TYPES.DROPDOWN,
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.cadence_label',
                defaultMessage: 'How often should this survey be sent?',
              }),
              objects: cadenceOptions,
              required: true,
              clearable: false,
              disabled: !isSurveyInDraft || isSaving,
            },
            ...SCHEDULE_INPUTS(formatMessage, {
              dateLabel: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.first_scheduled_time_label',
                defaultMessage: 'When should the first survey be sent?',
              }),
              value: settingsObj.scheduled_time as string | null,
              minDate: settingsObj.scheduled_time ? undefined : 'today',
              disabled: !isSurveyInDraft || isSaving,
              hideClearButton: !isSurveyInDraft,
            }),
          ]
        : []),
      ...(settingsObj?.survey_type === 'ONE_TIME'
        ? SCHEDULE_INPUTS(formatMessage, {
            dateLabel: formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.scheduled_time_label',
              defaultMessage: 'When should this survey be sent?',
            }),
            value: settingsObj.scheduled_time as string | null,
            minDate: settingsObj.scheduled_time ? undefined : 'today',
            disabled: !isSurveyInDraft || isSaving,
            hideClearButton: !isSurveyInDraft,
          })
        : []),
      ...(settingsObj?.survey_type === 'MILESTONE'
        ? [
            {
              name: 'milestone_field',
              type: INPUT_TYPES.DROPDOWN,
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_field_label',
                defaultMessage:
                  'Which milestone date do you want to anchor on?',
              }),
              objects: milestoneFieldOptions,
              required: true,
              clearable: false,
              disabled: !isSurveyInDraft || isSaving,
            },
            {
              name: 'milestone_days_offset',
              type: INPUT_TYPES.TEXT,
              inputType: 'number',
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_days_offset_label',
                defaultMessage: 'How many days after this milestone?',
              }),
              placeholder: formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.milestone_days_offset_placeholder',
                defaultMessage: 'e.g. 30',
              }),
              minValue: 0,
              required: true,
              disabled: !isSurveyInDraft || isSaving,
            },
          ]
        : []),
      {
        name: 'days_to_respond',
        type: INPUT_TYPES.TEXT,
        inputType: 'number',
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.days_to_respond_label',
          defaultMessage:
            'How many days should each participant have to respond?',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.custom_surveys.surveys.editor.days_to_respond_placeholder',
          defaultMessage: 'e.g. 7',
        }),
        minValue: 1,
        disabled: !isSurveyInDraft || isSaving,
      },
      {
        name: 'target_filter',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AdvancedCurrentPeopleApiFilter,
        label: (
          <TargetFilterLabel
            adminTargetAudience={
              survey?.admin_target_audience as unknown as ApiGroup
            }
            viewerIsOwner={viewerIsOwner}
          />
        ),
        disabled: isSaving,
      },
    ],
    [
      isSurveyInDraft,
      settingsObj?.survey_type,
      settingsObj?.scheduled_time,
      settingsObj?.target_filter,
      surveyTypes,
      cadenceOptions,
      milestoneFieldOptions,
      formatMessage,
      survey?.admin_target_audience,
      viewerIsOwner,
      isSaving,
    ]
  );

  const handleSettingsChange = useCallback((newObj: CustomSurveyData) => {
    setSettingsObj(newObj);
    setHasUnsavedChanges(true);
  }, []);

  const handleValidateSettings = useCallback(
    (obj: Record<string, unknown>) =>
      validateCustomSurveySettings(obj, formatMessage),
    [formatMessage]
  );

  // Questions that are not yet added to this survey (or added only with a filter,
  // so they can be added again for a different audience)
  const unselectedQuestions = useMemo(() => {
    const uuidsWithoutFilter = new Set(
      selectedQuestions
        .filter(
          (q) => !q.target_filter || Object.keys(q.target_filter).length === 0
        )
        .map((q) => q.question_uuid)
    );
    return availableQuestions.filter(
      (q) => q.uuid && !uuidsWithoutFilter.has(q.uuid) && q.status === 'ACTIVE'
    );
  }, [availableQuestions, selectedQuestions]);

  const handleAddQuestion = useCallback(
    (question: CustomSurveyQuestionData) => {
      if (!question.uuid) return;
      const questionObj = question.question as Record<string, unknown>;
      const newAssociation: SurveyQuestionAssociation = {
        question_uuid: question.uuid,
        order: selectedQuestions.length,
        required: true,
        target_filter: {},
        question_type: (questionObj?.type as string) || '',
        question_label: (questionObj?.label as string) || '',
        question_data: questionObj,
      };
      setSelectedQuestions((prev) => [...prev, newAssociation]);
      setHasUnsavedChanges(true);
    },
    [selectedQuestions.length]
  );

  const handleRemoveQuestion = useCallback((index: number) => {
    setSelectedQuestions((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((q, i) => ({ ...q, order: i }));
    });
    setHasUnsavedChanges(true);
  }, []);

  const handleMoveQuestion = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setSelectedQuestions((prev) => {
        if (
          dragIndex < 0 ||
          dragIndex >= prev.length ||
          hoverIndex < 0 ||
          hoverIndex >= prev.length
        ) {
          return prev;
        }
        const newList = [...prev];
        const [moved] = newList.splice(dragIndex, 1);
        newList.splice(hoverIndex, 0, moved);
        return newList.map((q, i) => ({ ...q, order: i }));
      });
      setHasUnsavedChanges(true);
    },
    []
  );

  const handleToggleRequired = useCallback((index: number) => {
    setSelectedQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, required: !q.required } : q))
    );
    setHasUnsavedChanges(true);
  }, []);

  const handleOpenFilterModal = useCallback(
    (index: number) => {
      const q = selectedQuestions[index];
      const existing =
        q.target_filter && Object.keys(q.target_filter).length > 0
          ? (q.target_filter as unknown as ApiGroup)
          : undefined;
      setPendingFilter(existing);
      setFilterEditingIndex(index);
    },
    [selectedQuestions]
  );

  const handleApplyFilter = useCallback(() => {
    if (filterEditingIndex === null) return;
    setSelectedQuestions((prev) =>
      prev.map((q, i) =>
        i === filterEditingIndex
          ? {
              ...q,
              target_filter: (pendingFilter as Record<string, unknown>) ?? {},
              // The edit may or may not actually resolve the mismatch
              // (that's only known after the next save), but clear the
              // stale error eagerly so the row doesn't keep showing a
              // warning about a filter that's already been changed.
              filterError: undefined,
            }
          : q
      )
    );
    setHasUnsavedChanges(true);
    setFilterEditingIndex(null);
    setPendingFilter(undefined);
  }, [filterEditingIndex, pendingFilter]);

  const handleCloseFilterModal = useCallback(() => {
    setFilterEditingIndex(null);
    setPendingFilter(undefined);
  }, []);

  const handleSave = useCallback(() => {
    if (!survey?.id || !settingsObj) return;
    setIsSaving(true);

    const questionsPayload = selectedQuestions.map((q, idx) => ({
      question_uuid: q.question_uuid,
      order: idx,
      required: q.required,
      target_filter: q.target_filter || {},
    }));

    const isMilestone = settingsObj.survey_type === 'MILESTONE';
    const isPeriodic = settingsObj.survey_type === 'PERIODIC';
    const isOneTime = settingsObj.survey_type === 'ONE_TIME';

    ConfirmAPI.sendRequestToConfirm(
      'PATCH',
      `custom-survey/surveys/${survey.id}`,
      {
        ...proxyParameters,
        questions: questionsPayload,
        expected_updated_at: survey.updated_at,
        name: settingsObj.name.trim(),
        description: settingsObj.description?.trim() ?? '',
        survey_type: settingsObj.survey_type,
        cadence: isPeriodic ? (settingsObj.cadence ?? null) : null,
        milestone_field: isMilestone
          ? (settingsObj.milestone_field ?? null)
          : null,
        milestone_days_offset: isMilestone
          ? settingsObj.milestone_days_offset != null
            ? Number(settingsObj.milestone_days_offset)
            : null
          : null,
        scheduled_time:
          (isOneTime || isPeriodic) && settingsObj.scheduled_time
            ? formatWallClockAsNaiveIsoString(
                new Date(settingsObj.scheduled_time)
              )
            : null,
        scheduled_timezone:
          isOneTime || isPeriodic
            ? ((settingsObj.scheduled_timezone as unknown as TimezoneOption)
                ?.value ?? null)
            : null,
        days_to_respond:
          settingsObj.days_to_respond != null
            ? Number(settingsObj.days_to_respond)
            : null,
        target_filter:
          (settingsObj.target_filter as Record<string, unknown>) ?? null,
        ...(settingsObj.survey_type === 'PER_PERSON'
          ? {
              // Sent rows stay in the table but are left out of the payload -
              // the server owns them, and sending them back would re-check a
              // recipient whose record has since changed and block the save.
              scheduled_participants: (settingsObj.scheduled_participants ?? [])
                .filter((participant) => !participant.sent_at)
                .map((participant) => ({
                  person_id: participant.person_id,
                  scheduled_time: formatWallClockAsNaiveIsoString(
                    new Date(participant.scheduled_time)
                  ),
                  scheduled_timezone: participant.scheduled_timezone,
                })),
            }
          : {}),
      },
      (data, error, hardErrorMessage) => {
        setIsSaving(false);
        if (error || hardErrorMessage) {
          // Keyed by order (as a string) - see the "questions" error shape
          // raised by _validate_question_filters_within_audience. order,
          // not question_uuid, since the same question can be added twice
          // with two different filters (see unselectedQuestions) - only
          // order tells those two rows apart. Attached directly onto each
          // row (rather than kept in separate state keyed by order) so it
          // travels with the row through any later reorder/removal for
          // free - the index correlation below only needs to hold for this
          // one synchronous update, since it's derived from the exact same
          // selectedQuestions array/order that was just submitted.
          const questionErrors: Record<string, string> =
            (error as { data?: { questions?: Record<string, string> } })?.data
              ?.questions ?? {};
          setSelectedQuestions((prev) =>
            prev.map((q, i) => ({
              ...q,
              filterError: questionErrors[String(i)],
            }))
          );

          const questionErrorCount = Object.keys(questionErrors).length;
          if (questionErrorCount > 0) {
            toast.error(
              formatMessage(
                {
                  id: 'app.views.administration.custom_surveys.surveys.editor.save_error_question_filters',
                  defaultMessage:
                    '{count, plural, one {One question filter needs updating} other {# question filters need updating}} - see the highlighted question(s) below.',
                },
                { count: questionErrorCount }
              )
            );
            return;
          }

          const backendMessage = getFriendlyErrorMessageIfErrorOccurred(
            error,
            hardErrorMessage
          );
          toast.error(
            backendMessage ??
              formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.save_error',
                defaultMessage: 'Failed to save survey.',
              })
          );
          return;
        }
        onSurveyChange(data);
        setSelectedQuestions(data.questions_at_latest_version || []);
        setHasUnsavedChanges(false);
        toast.success(
          formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.save_success',
            defaultMessage: 'Survey saved.',
          })
        );

        // The survey's own audience filter just changed, and at least one
        // question has its own filter - we can't tell whether those
        // question filters still target who the admin intends (e.g. the
        // audience narrowing from "person A" to "person B" doesn't make a
        // question filter of "department is A's department" wrong, just
        // possibly no longer what was meant), so nudge them to double check
        // rather than trying to guess. "Empty" is defined the same way the
        // backend's filter_has_values does (a non-empty `values` array) -
        // not just "the object has keys", since the filter picker can
        // round-trip "no filter" as {type, operator, values: []} rather
        // than undefined/null, and that shouldn't count as a change from
        // an actually-unset filter. Also order-independent - a group/
        // condition's values are a set, not a sequence, so removing
        // "Engineering" then re-adding it after "QA" changes the array's
        // order but not its meaning, and shouldn't count as a change
        // either (see canonicalizeApiFilter).
        const normalizeFilter = (filter: unknown) =>
          apiFilterHasValues(filter)
            ? canonicalizeApiFilter(filter)
            : undefined;
        const audienceFilterChanged = !isEqual(
          normalizeFilter(survey.target_filter),
          normalizeFilter(settingsObj.target_filter)
        );
        const hasQuestionFilters = selectedQuestions.some((q) =>
          apiFilterHasValues(q.target_filter)
        );
        if (audienceFilterChanged && hasQuestionFilters) {
          toast.info(
            formatMessage({
              id: 'app.views.administration.custom_surveys.surveys.editor.save_audience_filter_changed_warning',
              defaultMessage:
                'Target audience changed - review your question filters to make sure they still target the right people.',
            })
          );
        }
      }
    );
  }, [
    survey,
    selectedQuestions,
    settingsObj,
    proxyParameters,
    formatMessage,
    onSurveyChange,
  ]);

  const handleSaveClick = useCallback(() => {
    // submit the settings form so its onValidate runs before handleSave does
    settingsFormRef.current?.props.onSubmit();
  }, []);

  const handleBack = useCallback(() => {
    if (!canLeavePage()) return;
    history.push(consts.ADMINISTRATION_CUSTOM_SURVEYS(formatMessage).path);
  }, [canLeavePage, history, formatMessage]);

  const toggleQuestionEditor = useCallback(() => {
    setIsQuestionEditorOpen((prev) => !prev);
  }, []);

  const handleNewQuestionSave = useCallback(
    (newQuestion: UserGeneratedInputType) => {
      // Save the new question to the backend first, then add it to the survey
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        'custom-survey/questions/admin',
        {
          ...proxyParameters,
          question: newQuestion,
          status: 'ACTIVE',
        },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            toast.error(
              formatMessage({
                id: 'app.views.administration.custom_surveys.surveys.editor.create_question_error',
                defaultMessage: 'Failed to create question.',
              })
            );
            return;
          }
          // Add to available questions
          setAvailableQuestions((prev) => [data, ...prev]);
          // Add to selected questions
          handleAddQuestion(data);
        }
      );
    },
    [proxyParameters, formatMessage, handleAddQuestion]
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!survey) {
    return (
      <div className="container-fluid mt-4">
        <EmptyState
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.not_found',
            defaultMessage: 'Survey not found',
          })}
        >
          <Button color="primary" onClick={handleBack}>
            <FormattedMessage
              id="app.views.administration.custom_surveys.surveys.editor.back_to_surveys"
              defaultMessage="Back to surveys"
            />
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <AdvancedCurrentPeopleFilterContextProvider
      roleScope="CUSTOM_SURVEY"
      extras={{ surveyId: survey.id }}
    >
      <>
        {/* Status and save act on the survey's basics, so they sit with this
            tab's form rather than in the page header shared with Reports. */}
        <div className="d-flex justify-content-end align-items-center gap-2 mb-4">
          <span
            id="survey-status-dropdown-btn"
            style={
              hasUnsavedChanges || isReactivationBlocked
                ? { cursor: 'not-allowed' }
                : undefined
            }
          >
            <Dropdown
              isOpen={isStatusDropdownOpen}
              toggle={toggleStatusDropdown}
            >
              <DropdownToggle
                color={STATUS_COLOR[survey.status] ?? 'secondary'}
                caret
                disabled={hasUnsavedChanges || isReactivationBlocked}
                style={
                  hasUnsavedChanges || isReactivationBlocked
                    ? { pointerEvents: 'none' }
                    : undefined
                }
              >
                {surveyStatuses.find((s) => s.id === survey.status)?.name ??
                  survey.status}
              </DropdownToggle>
              <DropdownMenu end>
                {(STATUS_TRANSITIONS[survey.status] ?? []).map(
                  (targetStatus) => {
                    // A survey that never had a question added can't go
                    // Live or Archived - mirrors the backend's
                    // status_live_requires_questions/
                    // status_archived_requires_questions checks.
                    const isBlockedByNoQuestions =
                      (targetStatus === 'LIVE' ||
                        targetStatus === 'ARCHIVED') &&
                      selectedQuestions.length === 0;
                    const targetId = `survey-status-transition-${targetStatus}`;
                    const targetStatusName =
                      surveyStatuses.find((s) => s.id === targetStatus)?.name ??
                      targetStatus;
                    return (
                      <span id={targetId} key={targetStatus}>
                        <DropdownItem
                          onClick={() => handleStatusChange(targetStatus)}
                          disabled={isBlockedByNoQuestions}
                        >
                          {targetStatusName}
                        </DropdownItem>
                        {isBlockedByNoQuestions && (
                          <UncontrolledPopover
                            placement="left"
                            trigger="hover"
                            target={targetId}
                          >
                            <PopoverBody className="text-dark">
                              <FormattedMessage
                                id="app.views.administration.custom_surveys.surveys.editor.status.disabled_no_questions"
                                defaultMessage="Add at least one question before changing the status to {status}."
                                values={{ status: targetStatusName }}
                              />
                            </PopoverBody>
                          </UncontrolledPopover>
                        )}
                      </span>
                    );
                  }
                )}
              </DropdownMenu>
            </Dropdown>
          </span>
          {hasUnsavedChanges && (
            <UncontrolledPopover
              placement="bottom"
              trigger="hover"
              target="survey-status-dropdown-btn"
            >
              <PopoverBody className="text-dark">
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.editor.status.disabled_unsaved_changes"
                  defaultMessage="Save your changes before updating the survey status."
                />
              </PopoverBody>
            </UncontrolledPopover>
          )}
          {isReactivationBlocked && (
            <UncontrolledPopover
              placement="bottom"
              trigger="hover"
              target="survey-status-dropdown-btn"
            >
              <PopoverBody className="text-dark">
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.editor.status.disabled_one_time_already_sent"
                  defaultMessage="This is a one-time survey and has already been sent. It can't be reactivated."
                />
              </PopoverBody>
            </UncontrolledPopover>
          )}
          <Button
            color="primary"
            onClick={handleSaveClick}
            disabled={isSaving || isArchived}
          >
            {isSaving ? (
              <FormattedMessage
                id="app.views.administration.custom_surveys.surveys.editor.saving"
                defaultMessage="Saving..."
              />
            ) : (
              <FormattedMessage
                id="app.views.administration.custom_surveys.surveys.editor.save_button"
                defaultMessage="Save survey"
              />
            )}
          </Button>
        </div>

        {/* Survey settings */}
        <Card className="mb-4">
          <CardBody>
            <h4 className="mb-3">
              <FormattedMessage
                id="app.views.administration.custom_surveys.surveys.editor.settings_title"
                defaultMessage="Survey settings"
              />
            </h4>
            <ValidatedForm
              formRef={settingsFormRef}
              object={settingsObj}
              inputs={settingsInputs}
              onChange={handleSettingsChange}
              onValidate={handleValidateSettings}
              noValidate
              translationNamespace={`custom_survey_${survey.id}`}
              disableUnsavedChangesPrompt={true}
              readOnly={isArchived}
              renderInputs={(inputElements) => (
                <Row>
                  <Col md={6}>{inputElements.slice(0, -1)}</Col>
                  <Col md={6}>{inputElements[inputElements.length - 1]}</Col>
                </Row>
              )}
              renderForm={(children) => <>{children}</>}
              callback={handleSave}
            />
          </CardBody>
        </Card>

        <Row>
          {/* Left: Questions configuration */}
          <Col md={7}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.surveys.editor.questions_title"
                      defaultMessage="Questions"
                    />
                  </h4>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={toggleQuestionEditor}
                    disabled={isArchived || isSaving}
                  >
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.surveys.editor.create_question"
                      defaultMessage="Create question"
                    />
                  </Button>
                </div>

                {selectedQuestions.length === 0 ? (
                  <EmptyState
                    title={formatMessage({
                      id: 'app.views.administration.custom_surveys.surveys.editor.no_questions.title',
                      defaultMessage: 'No questions added yet',
                    })}
                    subtitle={formatMessage({
                      id: 'app.views.administration.custom_surveys.surveys.editor.no_questions.subtitle',
                      defaultMessage:
                        'Add existing questions from the list below or create a new one.',
                    })}
                    isMini={true}
                  />
                ) : (
                  <div className="list-group mb-3">
                    {selectedQuestions.map((q, index) => (
                      <SelectedQuestionRow
                        key={`${q.question_uuid}-${index}`}
                        association={q}
                        index={index}
                        questionTypes={questionTypes}
                        moveQuestion={handleMoveQuestion}
                        onToggleRequired={handleToggleRequired}
                        onOpenFilter={handleOpenFilterModal}
                        onRemove={handleRemoveQuestion}
                        disabled={isArchived || isSaving}
                        filterError={q.filterError}
                      />
                    ))}
                  </div>
                )}

                {/* Available questions to add */}
                {!isArchived && unselectedQuestions.length > 0 && (
                  <>
                    <h5 className="mt-4 mb-3">
                      <FormattedMessage
                        id="app.views.administration.custom_surveys.surveys.editor.available_questions"
                        defaultMessage="Available questions"
                      />
                    </h5>
                    <div className="list-group">
                      {unselectedQuestions.map((q) => {
                        const questionObj = q.question as Record<
                          string,
                          unknown
                        >;
                        return (
                          <div
                            key={q.id}
                            className="list-group-item d-flex align-items-center justify-content-between"
                          >
                            <div>
                              <RichTextViewer
                                model={(questionObj?.label as string) || '-'}
                              />
                              <small className="text-muted">
                                {questionTypes.find(
                                  (t) => t.id === questionObj?.type
                                )?.name || ''}
                              </small>
                            </div>
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => handleAddQuestion(q)}
                              disabled={isSaving}
                            >
                              <Icon className="me-1" icon={consts.ICONS.ADD} />
                              <FormattedMessage
                                id="app.views.administration.custom_surveys.surveys.editor.add"
                                defaultMessage="Add"
                              />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Right: Survey preview */}
          <Col md={5}>
            <Card>
              <CardBody>
                <h4 className="mb-3">
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.editor.preview_title"
                    defaultMessage="Survey preview"
                  />
                </h4>
                {selectedQuestions.length === 0 ? (
                  <p className="text-muted">
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.surveys.editor.preview_empty"
                      defaultMessage="Add questions to see a preview of your survey."
                    />
                  </p>
                ) : (
                  <ValidatedForm
                    object={previewObject}
                    inputs={previewInputs}
                    onChange={() => {}}
                    disableUnsavedChangesPrompt={true}
                    renderForm={(children) => <>{children}</>}
                    callback={() => {}}
                    autoFocus={false}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {filterEditingIndex !== null && survey.id && (
          <Modal isOpen size="lg" toggle={handleCloseFilterModal}>
            <ModalHeader toggle={handleCloseFilterModal}>
              <FormattedMessage
                id="app.views.administration.custom_surveys.surveys.editor.filter_modal_title"
                defaultMessage="Question audience filter"
              />
            </ModalHeader>
            <ModalBody>
              <p className="text-muted small mb-3">
                {viewerIsOwner ? (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.editor.filter_modal_help_owner"
                    defaultMessage="Restrict this question to a subset of survey participants. Leave empty to show to all participants. You can only add people within your HR administration scope in Confirm."
                  />
                ) : (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.surveys.editor.filter_modal_help_admin"
                    defaultMessage="Restrict this question to a subset of survey participants. Leave empty to show to all participants. You can only add people within the survey owner's HR administration scope in Confirm."
                  />
                )}
                <AdminScopeDisclosure
                  value={survey.admin_target_audience as unknown as ApiGroup}
                />
              </p>
              <AdvancedCurrentPeopleFilterContextProvider
                roleScope="CUSTOM_SURVEY_AUDIENCE"
                extras={{
                  surveyId: survey.id,
                  // Suggest against the in-progress (possibly unsaved)
                  // audience filter, not the stale one still in the DB -
                  // otherwise a person just added to the audience locally
                  // wouldn't be pickable here until the whole form is saved.
                  targetFilterOverride: settingsObj?.target_filter,
                }}
              >
                <AdvancedCurrentPeopleApiFilter
                  value={pendingFilter}
                  onChange={setPendingFilter}
                />
              </AdvancedCurrentPeopleFilterContextProvider>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={handleCloseFilterModal}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.editor.filter_modal_cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button color="primary" onClick={handleApplyFilter}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.surveys.editor.filter_modal_apply"
                  defaultMessage="Apply"
                />
              </Button>
            </ModalFooter>
          </Modal>
        )}

        <QuestionEditor
          isOpen={isQuestionEditorOpen}
          toggle={toggleQuestionEditor}
          callback={handleNewQuestionSave}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.question_editor_title',
            defaultMessage: 'Create question',
          })}
          submitText={formatMessage({
            id: 'app.views.administration.custom_surveys.surveys.editor.question_editor_submit',
            defaultMessage: 'Create question',
          })}
          hideAdvancedFeatures={true}
          allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
          excludeSpecialQuestions={EXCLUDE_SPECIAL_QUESTIONS}
        />
      </>
    </AdvancedCurrentPeopleFilterContextProvider>
  );
};

export default React.memo(CustomSurveyBasicsTab);
